import { useState, useEffect, useCallback } from 'react';
import { DashboardFilter, ReportType, ExportConfig, ExportColumn } from '../types/dashboard';
import { reportsService } from '../services/reports/reportsService';
import { generateExcelExport } from '../services/reports/excelExport';
import { useAuth } from '../context/AuthContext';

export function useReports() {
  const [reportTypes, setReportTypes] = useState<ReportType[]>([]);
  const [reportData, setReportData] = useState<Record<string, unknown>[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<DashboardFilter>({});
  const { user } = useAuth();

  const fetchReportTypes = useCallback(async () => {
    try {
      const res = await reportsService.getReportTypes();
      if (res.success) setReportTypes(res.data);
    } catch (err) {
      console.error('Failed to load report types');
    }
  }, []);

  useEffect(() => {
    fetchReportTypes();
  }, [fetchReportTypes]);

  const fetchReportData = useCallback(async (reportId: string) => {
    setLoading(true);
    setError(null);
    try {
      const res = await reportsService.getReportData(reportId, filter);
      if (res.success) {
        setReportData(res.data);
      } else {
        setError(res.message);
      }
    } catch (err) {
      setError('Failed to load report data');
    } finally {
      setLoading(false);
    }
  }, [filter]);

  const exportReport = useCallback((reportId: string, reportName: string, data: Record<string, unknown>[]) => {
    if (!data.length) return;

    const columns: ExportColumn[] = Object.keys(data[0]).map(key => ({
      key,
      header: key.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()),
      width: 18,
    }));

    const config: ExportConfig = {
      filename: `${reportId}-${new Date().toISOString().split('T')[0]}`,
      sheetName: reportName,
      columns,
      data,
      filters: filter,
      generatedAt: new Date().toISOString(),
      generatedBy: user?.name || 'System',
    };

    generateExcelExport(config);
  }, [filter, user]);

  return {
    reportTypes,
    reportData,
    loading,
    error,
    filter,
    setFilter,
    fetchReportData,
    exportReport,
  };
}
